import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, AlertCircle, Clock, ChevronDown, ChevronUp, Send, ArrowLeft, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SupportSolution, AIResolutionAttempt } from '@/types/support';
import { resolutionService } from '@/lib/support-docs/resolution-service';

interface SolutionPageProps {
  solution: SupportSolution | null;
  attempt: AIResolutionAttempt | null;
  ticket: any;
  newsletterColor: string;
  onBack: () => void;
  onCreateTicket: () => void;
  config: any;
  onChangeSolution?: (solutionId: string) => void;
}

export function SolutionPage({ 
  solution, 
  attempt,
  newsletterColor,
  onBack,
  onCreateTicket,
  config,
  onChangeSolution
}: SolutionPageProps) {
  const [feedback, setFeedback] = useState<'helpful' | 'not_helpful' | null>(null);
  const [expandedSteps, setExpandedSteps] = useState<number[]>([0]); // Primeiro passo expandido por padrão
  const [showRelated, setShowRelated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!solution || !attempt) return null;

  const relatedSolutions = resolutionService.findRelatedSolutions(solution.id);
  
  const handleRelatedClick = (relatedSolution: SupportSolution) => {
    if (onChangeSolution) {
      onChangeSolution(relatedSolution.id);
    }
    // Reset estados ao mudar de solução
    setFeedback(null);
    setExpandedSteps([0]);
    setShowRelated(false);
  };
  const confidencePercentage = Math.round(attempt.confidenceScore * 100);

  const toggleStep = (order: number) => {
    setExpandedSteps(prev => 
      prev.includes(order) 
        ? prev.filter(o => o !== order)
        : [...prev, order]
    );
  };

  const handleFeedback = async (value: 'helpful' | 'not_helpful') => {
    setFeedback(value);
    setIsSubmitting(true);
    
    await resolutionService.processFeedback(
      attempt.ticketId,
      value,
      value === 'not_helpful'
    );
    
    setIsSubmitting(false);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="min-h-screen bg-gray-50 flex items-center justify-center pt-8"
      style={{ '--newsletter-color': newsletterColor } as React.CSSProperties}
    >
      <div className="w-full max-w-5xl p-6">
        {/* Logo e Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col items-center text-center mb-8"
        >
          <img 
            src={config.logo}
            alt={config.title}
            className="w-48 mx-auto mb-6"
          />
        </motion.div>

        {/* Card Principal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100 max-w-3xl mx-auto"
        >
          {!feedback ? (
            <>
              {/* Header da Solução */}
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" 
                       style={{ backgroundColor: newsletterColor }}>
                    <CheckCircle className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    Encontramos uma solução!
                  </h2>
                </div>
                
                <p className="text-gray-600 mb-4">
                  Com base na sua descrição, identificamos o problema e temos uma solução que pode ajudar.
                </p>
                
                {/* Badge de Confiança */}
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border"
                     style={{ borderColor: newsletterColor, backgroundColor: `${newsletterColor}15` }}>
                  <div className="w-2 h-2 rounded-full animate-pulse" 
                       style={{ backgroundColor: newsletterColor }} />
                  <span style={{ color: newsletterColor }}>
                    Confiança: {confidencePercentage}%
                  </span>
                </div>
              </div>

              {/* Título e Descrição da Solução */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {solution.title}
                </h3>
                <p className="text-gray-600">
                  {solution.content}
                </p>
                
                {/* Informações Rápidas */}
                <div className="flex gap-4 mt-4 text-sm">
                  {solution.timeToResolve && (
                    <div className="flex items-center gap-1 text-gray-500">
                      <Clock className="w-4 h-4" />
                      <span>Tempo estimado: {solution.timeToResolve}</span>
                    </div>
                  )}
                  {solution.successRate && (
                    <div className="flex items-center gap-1" style={{ color: newsletterColor }}>
                      <CheckCircle className="w-4 h-4" />
                      <span>Taxa de sucesso: {Math.round(solution.successRate * 100)}%</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Passos da Solução */}
              <div className="space-y-3 mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">
                  Passos para resolver:
                </h4>
                
                {solution.steps.map((step) => (
                  <motion.div
                    key={step.order}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: step.order * 0.1 }}
                    className="border border-gray-200 rounded-xl overflow-hidden"
                  >
                    <button
                      onClick={() => toggleStep(step.order)}
                      className="w-full p-4 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm"
                             style={{ backgroundColor: newsletterColor }}>
                          {step.order}
                        </div>
                        <div>
                          <span className="font-medium text-gray-900">
                            {step.title}
                          </span>
                          {step.isOptional && (
                            <span className="ml-2 text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                              Opcional
                            </span>
                          )}
                        </div>
                      </div>
                      {expandedSteps.includes(step.order) ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    
                    <AnimatePresence>
                      {expandedSteps.includes(step.order) && (
                        <motion.div
                          initial={{ height: 0 }}
                          animate={{ height: 'auto' }}
                          exit={{ height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="p-4 bg-white border-t border-gray-200">
                            <p className="text-gray-700">
                              {step.description}
                            </p>
                            
                            {step.warningMessage && (
                              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                <p className="text-sm text-amber-800">
                                  {step.warningMessage}
                                </p>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>

              {/* Ações Necessárias */}
              {solution.requiredActions && solution.requiredActions.length > 0 && (
                <div className="mt-6 mb-6 p-4 rounded-xl border" 
                     style={{ backgroundColor: `${newsletterColor}10`, borderColor: `${newsletterColor}30` }}>
                  <h4 className="font-semibold mb-2" style={{ color: newsletterColor }}>
                    ⚡ Ações necessárias:
                  </h4>
                  <ul className="space-y-1">
                    {solution.requiredActions.map((action, index) => (
                      <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                        <span style={{ color: newsletterColor }} className="mt-1">•</span>
                        <span>{action.description}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Soluções Relacionadas */}
              {relatedSolutions.length > 0 && (
                <div className="mb-8">
                  <button
                    onClick={() => setShowRelated(!showRelated)}
                    className="text-sm hover:underline flex items-center gap-1 font-medium"
                    style={{ color: newsletterColor }}
                  >
                    <span>
                      {showRelated ? 'Ocultar' : 'Ver'} outros problemas similares ({relatedSolutions.length})
                    </span>
                    {showRelated ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  
                  <AnimatePresence>
                    {showRelated && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-3 space-y-2 overflow-hidden"
                      >
                        {relatedSolutions.map((related) => (
                          <button
                            key={related.id}
                            onClick={() => handleRelatedClick(related)}
                            className="w-full text-left p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-all cursor-pointer group border border-transparent hover:border-gray-200"
                          >
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                                  <p className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                                    {related.title}
                                  </p>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-gray-500">
                                  <span className="flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3" />
                                    Taxa de sucesso: {Math.round(related.successRate * 100)}%
                                  </span>
                                  {related.timeToResolve && (
                                    <span className="flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      {related.timeToResolve}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-gray-600 transform group-hover:translate-x-1 transition-transform" />
                            </div>
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}

              {/* Botões de Feedback */}
              <div className="space-y-4 border-t border-gray-200 pt-6">
                <p className="text-center text-gray-700 font-medium">
                  Esta solução foi útil?
                </p>
                <div className="flex gap-3 justify-center">
                  <Button
                    onClick={() => handleFeedback('helpful')}
                    disabled={isSubmitting}
                    className="flex items-center gap-2 text-white"
                    style={{ backgroundColor: newsletterColor, borderColor: newsletterColor }}
                  >
                    <CheckCircle className="w-5 h-5" />
                    Sim, resolveu!
                  </Button>
                  <Button
                    onClick={() => handleFeedback('not_helpful')}
                    disabled={isSubmitting}
                    variant="outline"
                    className="flex items-center gap-2 border-gray-300 text-gray-600 hover:bg-gray-50"
                  >
                    <XCircle className="w-5 h-5" />
                    Não resolveu
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-6"
            >
              {feedback === 'helpful' ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                       style={{ backgroundColor: `${newsletterColor}20` }}>
                    <CheckCircle className="w-10 h-10" style={{ color: newsletterColor }} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Ótimo! Ficamos felizes em ajudar!
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Seu problema foi resolvido com sucesso.
                  </p>
                  <Button
                    onClick={onBack}
                    className="text-white"
                    style={{ backgroundColor: newsletterColor }}
                  >
                    <ArrowLeft className="w-5 h-5 mr-2" />
                    Voltar ao início
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="text-center">
                    <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <AlertCircle className="w-10 h-10 text-amber-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      Vamos conectar você com nossa equipe
                    </h3>
                    <p className="text-gray-600">
                      Um especialista humano irá ajudar você com este problema.
                    </p>
                  </div>
                  
                  <div className="flex gap-3 justify-center">
                    <Button
                      onClick={onBack}
                      variant="outline"
                      className="flex items-center gap-2"
                    >
                      <ArrowLeft className="w-5 h-5" />
                      Voltar
                    </Button>
                    <Button
                      onClick={onCreateTicket}
                      className="flex items-center gap-2 text-white"
                      style={{ backgroundColor: newsletterColor }}
                    >
                      <Send className="w-5 h-5" />
                      Criar ticket para suporte
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </motion.div>

        {/* Info Box */}
        {!feedback && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-gray-50 rounded-xl p-4 border border-gray-200 text-gray-700 text-center max-w-2xl mx-auto mt-6"
          >
            <p className="text-sm">
              💡 Se precisar de ajuda adicional ou tiver dúvidas sobre os passos, 
              nossa equipe de suporte está disponível para auxiliar você.
            </p>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}